import { createContext, useContext, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'

import { useSiteTimezone } from '../pages/reports/components/timezone-indicator/use-site-timezone.hook'

/**
 * Site-scoped IANA timezone, sourced ONCE per site navigation from
 * BigQuery via `AnalyticsSiteAttributes.timezone`.
 *
 * Placed at the `/s/:siteId/*` route root (inside `SiteUrlSync`) so
 * every landing page, reports tab, and future analytics dashboard
 * reads the same authoritative value — no per-page BQ query, no
 * cold-cache races, no navigation-time flicker between Melbourne
 * and Sydney.
 *
 * The context stays `null` while the BQ query resolves; consumers
 * render a skeleton or hide until it lands. Never fall through to
 * Spanner (`siteQuery.site(...).timezone` returns legacy `'AEST'`
 * which mis-maps to Sydney) — Cube is our single source of truth
 * for site attributes.
 *
 * At brand scope (no `siteId` in the route params) the context is
 * `null` — brand-level pages render "Time in UTC" as a static label
 * per the ticket's brand-stays-UTC-by-design rule.
 */

const SiteTimezoneContext = createContext<string | null>(null)

export const SiteTimezoneProvider = ({ children }: { children: ReactNode }) => {
  const { siteId } = useParams()
  const warehouseTimezone = useSiteTimezone(siteId)
  return (
    <SiteTimezoneContext.Provider value={warehouseTimezone}>
      {children}
    </SiteTimezoneContext.Provider>
  )
}

/**
 * Read the site's IANA timezone (e.g. `'Australia/Melbourne'`).
 *
 * Returns `null` while BigQuery is resolving — every caller MUST
 * handle that explicitly (skeleton, hide, defer render). Never
 * substitute a Spanner-derived fallback.
 */
export function useSiteTimezoneContext(): string | null {
  return useContext(SiteTimezoneContext)
}
